/** Types for ML Studio: artifacts, workflow runs, and Dagster data */

// ── Status pill colours ─────────────────────────────────────────────────────
// The fill and the word colour of each status pill (design system README,
// "Status"). Green is for running work only. A done status is neutral. A
// fault is slate on light and off-white on dark. The `*_STATUS_STYLES` and
// `*_STATUS_COLORS` maps below use these values. Callers spread a map entry
// into `StatusBadge` from `@bastion/ui`.

type StatusPillStyle = { background: string; color: string };

const RUNNING_PILL: StatusPillStyle = {
  background: 'var(--pill-running-bg)',
  color: 'var(--pill-running-text)',
};
const NEUTRAL_PILL: StatusPillStyle = {
  background: 'var(--pill-neutral-bg)',
  color: 'var(--text)',
};
const FAULT_PILL: StatusPillStyle = {
  background: 'var(--pill-fault-bg)',
  color: 'var(--pill-fault-text)',
};
const OFFLINE_PILL: StatusPillStyle = {
  background: 'var(--pill-neutral-bg)',
  color: 'var(--text-3)',
};

// ── Artifact Types ──────────────────────────────────────────────────────────

export type ArtifactType =
  | 'sft_model'
  | 'reward_model'
  | 'labeled_dataset'
  | 'trained_policy'
  | 'lerobot_dataset'
  | 'reward_scores'
  | 'embeddings'
  | 'recap_policy';
export type ArtifactStatus = 'producing' | 'ready' | 'failed';

export interface Artifact {
  id: string;
  type: ArtifactType;
  name: string;
  s3_path: string;
  status: ArtifactStatus;
  config: Record<string, unknown>;
  inputs: Record<string, string>; // role -> artifact_id
  checkpoints: Array<{ step: number; s3_path: string }>;
  dagster_run_id: string | null;
  workflow_run_id: string | null;
  experiment_id?: string | null;
  snapshot_id: string | null;
  git_ref: string | null;
  created_at: string;
  created_by: string | null;
}

export interface PolicyPerformance {
  artifact_id: string;
  artifact_name: string;
  artifact_type: ArtifactType;
  sop_ids: string[];
  missing_sop_episode_count: number;
  total_episodes: number;
  eligible_timed_eval_rollouts: number;
  fully_autonomous_rollouts: number;
  intervened_rollouts: number;
  fully_autonomous_rate: number | null;
  rating_counts: Record<string, number>;
  mean_autonomous_rollout_elapsed_seconds: number | null;
  /**
   * Mean autonomous-time fraction averaged ONLY over eligible timed-eval
   * rollouts. The `eligible_` prefix flags the denominator change vs. the
   * pre-PR field which spanned all inference episodes.
   */
  mean_eligible_autonomous_time_fraction: number | null;
  created_at: string;
}

export const ARTIFACT_TYPE_LABELS: Record<ArtifactType, string> = {
  sft_model: 'SFT model',
  reward_model: 'Reward model',
  labeled_dataset: 'Labeled dataset',
  trained_policy: 'Trained policy',
  lerobot_dataset: 'LeRobot dataset',
  reward_scores: 'Reward scores',
  embeddings: 'Embeddings',
  recap_policy: 'RECAP policy',
};

// ── Workflow Run Types ──────────────────────────────────────────────────────

export type WorkflowRunStatus = 'draft' | 'waiting_for_gpu' | 'running' | 'completed' | 'failed';
/** @deprecated Use WorkflowRunStatus */
export type ExperimentStatus = WorkflowRunStatus;

export const WORKFLOW_RUN_STATUS_COLORS: Record<WorkflowRunStatus, { background: string; color: string }> = {
  draft: NEUTRAL_PILL,
  waiting_for_gpu: NEUTRAL_PILL,
  running: RUNNING_PILL,
  completed: NEUTRAL_PILL,
  failed: FAULT_PILL,
};
/** @deprecated Use WORKFLOW_RUN_STATUS_COLORS */
export const EXPERIMENT_STATUS_COLORS = WORKFLOW_RUN_STATUS_COLORS;

export const WORKFLOW_RUN_STATUS_LABELS: Record<WorkflowRunStatus, string> = {
  draft: 'Draft',
  waiting_for_gpu: 'Waiting for GPU',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
};
/** @deprecated Use WORKFLOW_RUN_STATUS_LABELS */
export const EXPERIMENT_STATUS_LABELS = WORKFLOW_RUN_STATUS_LABELS;

export const WORKFLOW_STATUS_COLORS = WORKFLOW_RUN_STATUS_COLORS;
export const WORKFLOW_STATUS_LABELS = WORKFLOW_RUN_STATUS_LABELS;
export type WorkflowStatus = WorkflowRunStatus;
export type Workflow = WorkflowRun;

export type OpStatus = 'queued' | 'running' | 'completed' | 'failed' | 'skipped';

export interface Op {
  name: string;
  step_type: string;
  config: Record<string, unknown>;
  status: OpStatus;
  artifact_id: string | null;
  gpus: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface OpDep {
  from_op: string;
  to_op: string;
  role: string | null;
}

export interface WorkflowRun {
  id: string;
  name: string;
  description: string | null;
  status: WorkflowRunStatus;
  gpu_instance_type: string | null;
  created_at: string;
  created_by: string | null;
  workflow_name?: string | null;
  hypothesis: string | null;
  learnings: string | null;
  evaluation: string | null;
  tags: string[];
  ops?: Op[];
  op_deps?: OpDep[];
  /** Deduped dataset IDs this run touches, resolved server-side via
   *  ``ml_artifacts.snapshot_id → dataset_snapshots.dataset_id``. Empty
   *  for runs without snapshot-bound artifacts (e.g. still queued). */
  dataset_ids?: string[];
}
/** @deprecated Use WorkflowRun */
export type Experiment = WorkflowRun;

export interface WorkflowRunTagCount {
  tag: string;
  count: number;
}

export interface WorkflowRunLaunchResult {
  workflow_run_id: string;
  experiment_id?: string;
  gpu_job_id: string | null;
  error: string | null;
}
/** @deprecated Use WorkflowRunLaunchResult */
export type ExperimentLaunchResult = WorkflowRunLaunchResult;

// ── Workflow Run Metrics Types ──────────────────────────────────────────────

export interface ArtifactMetricsEntry {
  artifact_id: string;
  artifact_type: string;
  artifact_name: string;
  artifact_status: string;
  metrics: Record<string, number>[];
}

export type WorkflowRunMetricsResponse = Record<string, ArtifactMetricsEntry>;
/** @deprecated Use WorkflowRunMetricsResponse */
export type ExperimentMetricsResponse = WorkflowRunMetricsResponse;

export interface ArtifactLogChunkResponse {
  content: string;
  offset: number;
  next_offset: number;
}

// ── GPU Types (re-exported from @bastion/gpu) ──────────────────────────────

export type { LambdaInstanceType } from '@bastion/gpu';

export type GpuJobStatus =
  | 'queued' | 'provisioning' | 'active' | 'running'
  | 'terminating' | 'terminated' | 'failed' | 'cancelled';

export interface GpuJob {
  id: string;
  workflow_run_id: string | null;
  experiment_id?: string | null;
  instance_type: string;
  region: string | null;
  status: GpuJobStatus;
  lambda_instance_id: string | null;
  ssh_ip: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
  terminated_at: string | null;
}

export function isGpuJobActive(status: GpuJobStatus): boolean {
  return ['queued', 'provisioning', 'active', 'running'].includes(status);
}

const UPPERCASE_ABBREVS = new Set(['sft', 'iql', 'ddp', 'rm', 'lr', 'bs', 'rl']);

/**
 * Convert a stage or op name to a sentence-case display label.
 *
 * The function replaces underscores with spaces, capitalizes the first word,
 * and writes known abbreviations in capitals. Other words keep their case.
 * Example: "iql_control" becomes "IQL control".
 */
export function humanizeStageName(stageName: string): string {
  let first = true;
  return stageName
    .replace(/_/g, ' ')
    .replace(/\b\w+/g, w => {
      const isFirst = first;
      first = false;
      if (UPPERCASE_ABBREVS.has(w.toLowerCase())) return w.toUpperCase();
      return isFirst ? w.charAt(0).toUpperCase() + w.slice(1) : w;
    });
}

// ── Pinned Checkpoint Types ──────────────────────────────────────────────────

export type PinnedCheckpointStatus = 'active' | 'completed' | 'archived';

/** Per-subtask duration statistics computed from the policy's training
 *  dataset. Stored on `pinned_checkpoints.subtask_timeout_stats` and
 *  used by the desktop Evaluator to auto-enter intervention mode.
 */
export interface SubtaskTimeoutEntry {
  /** Index into the SOP's `subtask_labels`, or null for orphan labels. */
  index: number | null;
  label: string;
  sample_count: number;
  p95_seconds: number;
  timeout_seconds: number;
}

export interface SubtaskTimeoutStats {
  computed_at: string;
  dataset_id: string | null;
  sop_id: string | null;
  multiplier: number;
  min_samples: number;
  subtasks: SubtaskTimeoutEntry[];
  /** SOP labels without enough samples to compute a reliable p95. */
  missing_labels: string[];
}

/** Slim per-subtask timeout list suitable for passing to the desktop
 *  evaluator. SOP-ordered entries carry a numeric `index`; orphan
 *  entries (no matching SOP label, or missing SOP) have `index: null`
 *  and are matched on the Python side by label instead.
 */
export interface SubtaskTimeout {
  index: number | null;
  label: string;
  timeout_seconds: number;
}

/** Per-subtask frame-based rollout metrics, derived from manual subtask
 *  segments on the pin's inference episodes + the per-frame
 *  intervention ranges stored on episodes. Null values mean either
 *  the subtask has not yet been observed on this pin, or the
 *  underlying episodes don't yet have `intervention_ranges` populated.
 */
export interface SubtaskRolloutMetrics {
  index: number | null;
  label: string;
  /** Number of segment occurrences for this subtask across all rollouts on
   *  this pin (a single episode can contribute multiple segments). */
  segment_count: number;
  /** Average per-segment duration -- per-occurrence, not per-episode. */
  mean_duration_seconds: number | null;
  percent_autonomous: number | null;
  completion_score_sum: number;
  completion_count: number;
  timeout_seconds: number | null;
}

/** Per-pin frame-based rollout metrics computed from inference episodes. */
export interface RolloutMetrics {
  mean_episode_duration_seconds: number | null;
  /** Non-intervention frames / total frames, across all rollouts on this pin. */
  percent_episode_autonomous: number | null;
  /** Sum of the subtask completion score across every subtask occurrence.
   *  Null when the pin has no rollouts yet (so the UI can render an
   *  em-dash matching the other empty metrics). */
  subtask_completion_score_sum: number | null;
  subtasks: SubtaskRolloutMetrics[];
}

export interface EvalTimeoutMetrics {
  eval_episode_count: number;
  successful_autonomous_subtasks: number;
  total_expected_subtasks: number;
  autonomous_subtask_success_rate: number | null;
  fully_autonomous_episode_count: number;
  fully_autonomous_episode_rate: number | null;
}

export interface PolicyEvalBestCheckpoint {
  pinned_checkpoint_id: string;
  artifact_id: string;
  artifact_name: string | null;
  checkpoint_step: number | null;
  checkpoint_label: string | null;
  fully_autonomous_rollouts: number;
  total_rollouts: number;
  fully_autonomous_rollout_rate: number | null;
}

export interface PolicyEvalSopSummary {
  base_sop_id: string;
  base_sop_title: string;
  current_eval_sop_id: string | null;
  current_eval_sop_version: number | null;
  latest_result_eval_sop_id: string | null;
  latest_result_eval_sop_version: number | null;
  current_version_has_results: boolean;
  result_checkpoint_count: number;
  best_checkpoint: PolicyEvalBestCheckpoint | null;
  last_evaluated_at: string | null;
}

export interface PolicyEvalExpectedSubtask {
  index: number | null;
  label: string;
  timeout_seconds: number;
  expected_count: number;
}

export interface PolicyEvalSubtaskMetric {
  autonomous_attempts: number;
  attempted: number;
  expected: number;
  rate: number | null;
}

export interface PolicyEvalCheckpointRow {
  pinned_checkpoint_id: string;
  artifact_id: string;
  artifact_name: string | null;
  artifact_type: ArtifactType | null;
  checkpoint_step: number | null;
  checkpoint_label: string | null;
  status: PinnedCheckpointStatus | null;
  fully_autonomous_rollouts: number;
  total_rollouts: number;
  fully_autonomous_rollout_rate: number | null;
  autonomous_attempted_subtasks: number;
  attempted_subtasks: number;
  subtask_completion_rate: number | null;
  subtasks: Record<string, PolicyEvalSubtaskMetric>;
  last_evaluated_at: string | null;
}

export interface PolicyEvalSopDetail {
  base_sop_id: string;
  base_sop_title: string;
  eval_sop_id: string | null;
  eval_sop_version: number | null;
  available_eval_versions: Array<{
    eval_sop_id: string;
    eval_sop_version: number;
    last_evaluated_at: string | null;
  }>;
  available_hostnames: string[];
  expected_subtasks: PolicyEvalExpectedSubtask[];
  rows: PolicyEvalCheckpointRow[];
}

export interface PinnedCheckpoint {
  id: string;
  artifact_id: string;
  checkpoint_step: number | null;
  checkpoint_s3_path: string;
  label: string;
  target_rollouts: number;
  notes: string | null;
  status: PinnedCheckpointStatus;
  pinned_by: string | null;
  pinned_at: string;
  completed_at: string | null;
  completed_rollouts: number;
  fully_autonomous_count: number;
  intervened_rollouts: number;
  fully_autonomous_rate: number | null;
  rating_counts: Record<string, number>;
  mean_autonomous_rollout_elapsed_seconds: number | null;
  mean_autonomous_time_fraction: number | null;
  artifact_name: string | null;
  artifact_type: ArtifactType | null;
  subtask_timeout_stats?: SubtaskTimeoutStats | null;
  subtask_timeouts?: SubtaskTimeout[] | null;
  rollout_metrics?: RolloutMetrics | null;
  eval_timeout_metrics?: EvalTimeoutMetrics | null;
}

export interface PinCheckpointInput {
  artifact_id: string;
  checkpoint_step: number | null;
  checkpoint_s3_path: string;
  label: string;
  target_rollouts?: number;
  notes?: string;
}

export const PINNED_CHECKPOINT_STATUS_STYLES: Record<PinnedCheckpointStatus, { background: string; color: string }> = {
  active: RUNNING_PILL,
  completed: NEUTRAL_PILL,
  archived: NEUTRAL_PILL,
};

// ── Policy Catalog Types (GET /ml/policies/catalog) ─────────────────────────

/**
 * Where a policy was trained. `parametric` is an `ml_artifacts` row of type
 * `sft_model`, `trained_policy` or `recap_policy`. `pi` is a finetune run in
 * the Physical Intelligence Partner Finetuning API.
 */
export type PolicySource = 'parametric' | 'pi';

/**
 * Robot performance of one policy or one checkpoint. The server counts
 * inference episodes and leaves out test episodes: episodes shorter than
 * 5 s, and episodes with a tag `smoke-test`, `leader:simulated`,
 * `simulated-policy` or `hardware-test`, a tag that ends in `-hardware-test`,
 * or a tag that starts with `diagnostic:`.
 */
export interface PolicyRobotPerformance {
  /** Counted robot inference episodes. */
  rollouts: number;
  /** Sum of the counted episode durations, in seconds. */
  robot_seconds: number;
  /**
   * 1 - (sum of intervention seconds / sum of duration seconds), over the
   * counted episodes with known intervention ranges and a known duration.
   * Null when there is no such episode. It measures operator time only, not
   * task success.
   */
  autonomous_fraction: number | null;
  /** Counted episodes whose intervention ranges are a known empty list. */
  fully_autonomous_rollouts: number;
  /** Counted episodes whose intervention ranges are known (empty or not). */
  rollouts_with_known_interventions: number;
  /** fully_autonomous_rollouts / rollouts_with_known_interventions, or null. */
  fully_autonomous_rate: number | null;
  /** Number of intervention ranges over the counted episodes with known ranges. */
  interventions: number;
  /**
   * Counted episodes with a known outcome. `parametric`: a `task_rating`.
   * `pi`: an `ai:end_state=<value>` tag.
   */
  rollouts_with_outcome: number;
  /**
   * Counted episodes with a good outcome. `parametric`: `task_rating` 3
   * (partial) or 5 (perfect). `pi`: the tag `ai:end_state=ok`.
   */
  successful_rollouts: number;
  /** successful_rollouts / rollouts_with_outcome, or null. */
  success_rate: number | null;
  last_rollout_at: string | null;
  /** Distinct robot hostnames, most recent first. */
  robots: string[];
}

export interface PolicyCatalogEntry {
  /** `ml_artifacts.id` for `parametric`; the Pi run UUID for `pi`. */
  id: string;
  source: PolicySource;
  name: string;
  /**
   * `parametric`: the artifact type (`sft_model`, `trained_policy` or
   * `recap_policy`). `pi`: `pi_finetune`.
   */
  kind: string;
  /** `parametric`: `config.base_model`, else `config.policy_type`. `pi`: the Pi base model, e.g. `pi07`. */
  base_model: string | null;
  /** One line. `parametric`: from the workflow run. `pi`: the hypothesis of the matching Pi dataset export. */
  description: string | null;
  created_at: string;
  /**
   * `parametric`: the artifact status (`ready`, or `producing` or `failed` for
   * an artifact that has a pin). `pi`: the lowercase run state, for example
   * `succeeded` or `running`.
   */
  status: string;
  /** Pi training progress from 0 to 1 while the run is not terminal. Null otherwise. */
  progress: number | null;
  train_steps: number | null;
  checkpoint_count: number;
  /** `parametric` only: pins that are not archived. 0 for `pi`. */
  pinned_count: number;
  performance: PolicyRobotPerformance;
}

export type PiCatalogState = 'ok' | 'not_configured' | 'error';

export interface PiCatalogStatus {
  status: PiCatalogState;
  /** Short human-readable reason when status is not `ok`. */
  message: string | null;
  /** When the server last read the Pi catalog. Null when it never did. */
  fetched_at: string | null;
}

export interface PolicyCatalogResponse {
  policies: PolicyCatalogEntry[];
  pi: PiCatalogStatus;
}

// ── Pi Policy Detail Types (GET /ml/policies/pi/{run_id}) ───────────────────

export interface PiCheckpointPolicy {
  /** `final_only` or `every_n_steps`. */
  mode: string;
  every_n_steps: number | null;
  limit: number | null;
}

export interface PiRunInfo {
  run_id: string;
  name: string;
  state: string;
  base_model: string | null;
  continue_from_checkpoint_id: string | null;
  num_steps: number | null;
  batch_size: number | null;
  checkpoint_policy: PiCheckpointPolicy | null;
  training_profile_id: string | null;
  /** For example `parametric-mk02-arms-neck-4rgb-pt31-absolute-v1`. */
  training_profile_name: string | null;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  terminal_reason: string | null;
  progress: number | null;
}

export interface PiCheckpointSummary {
  checkpoint_id: string;
  step: number;
  /** Lowercase Pi checkpoint state: `pending`, `available` or `failed`. */
  state: string;
  created_at: string | null;
  available_at: string | null;
  /** The `wss://api.pi-fleet.com/v1/models/...` URL an operator loads in the Policy controls. */
  gateway_url: string | null;
  /** Validation MAE at this step, when Pi reported one. */
  validation_mae: number | null;
  train_mae: number | null;
  performance: PolicyRobotPerformance;
}

export interface PiEvaluationPoint {
  step: number;
  train_mae: number;
  train_mse: number;
  validation_mae: number | null;
  validation_mse: number | null;
  /** The dataset filter of this row. A run with two filters has two rows per step. */
  dataset_filter_id: string | null;
}

export interface PiDatasetFilterSummary {
  dataset_filter_id: string;
  name: string | null;
  /** Null when the run gives no weight. */
  weight: number | null;
  /** Number of included episode IDs, when the filter lists them. */
  episode_count: number | null;
  robot_platform: string | null;
  created_at: string | null;
}

export interface PiDatasetExportSummary {
  /** The `ml_artifacts.id` of the `pi_dataset_export` row. */
  artifact_id: string;
  name: string;
  created_at: string;
  hypothesis: string | null;
  /** The prompt the training data was exported with. */
  training_prompt: string | null;
  variant: string | null;
  source_episodes: number | null;
  kept_frames: number | null;
  sop_id: string | null;
  /** The Pi run the hypothesis compares against (`config.comparison_run_id`). */
  comparison_run_id: string | null;
  /** Its name, when the Pi catalog has the run. */
  comparison_run_name: string | null;
}

export interface PiPromptUsage {
  prompt: string;
  rollouts: number;
  last_used_at: string;
}

export interface PiRolloutSummary {
  episode_id: string;
  created_at: string;
  hostname: string | null;
  checkpoint_step: number | null;
  pi_checkpoint_id: string | null;
  duration_seconds: number | null;
  intervention_seconds: number | null;
  /** Number of intervention ranges, or null when the ranges are unknown. */
  interventions: number | null;
  /** Value of the `ai:end_state=<value>` tag, for example `ok` or `fail`. */
  end_state: string | null;
  /** False for test episodes, which the performance numbers leave out. */
  counted: boolean;
}

export interface PiPolicyDetailResponse {
  policy: PolicyCatalogEntry;
  run: PiRunInfo;
  /** Ascending by step. */
  checkpoints: PiCheckpointSummary[];
  /** Ascending by step. */
  evaluations: PiEvaluationPoint[];
  dataset_filters: PiDatasetFilterSummary[];
  dataset_export: PiDatasetExportSummary | null;
  /** Inference prompts used on robots, most used first. */
  prompts: PiPromptUsage[];
  /** Newest first, at most 50. Includes test episodes with `counted: false`. */
  recent_rollouts: PiRolloutSummary[];
}

// ── Dagster Run Types ────────────────────────────────────────────────────────

export type RunStatus =
  | 'NOT_STARTED' | 'STARTING' | 'STARTED' | 'SUCCESS'
  | 'FAILURE' | 'CANCELING' | 'CANCELED' | 'QUEUED' | 'MANAGED';

export interface RunInfo {
  run_id: string;
  job_name: string | null;
  status: RunStatus;
  start_time: number | null;
  end_time: number | null;
  tags: Record<string, string>;
}

export interface LogEvent {
  typename: string;
  message: string;
  timestamp: string;
  level: string | null;
  step_key: string | null;
}

export interface RunLogsResponse {
  events: LogEvent[];
  cursor: string | null;
  has_more: boolean;
}

export interface AssetInfo {
  key: string[];
  description: string | null;
  job_names: string[];
  group_name: string | null;
  kinds: string[];
  latest_materialization: {
    run_id: string | null;
    timestamp: string | null;
    metadata: Record<string, string | number | null> | null;
  } | null;
  deps: string[][];
}

export interface JobInfo {
  name: string;
  description: string | null;
}

export interface LaunchRunRequest {
  job_name: string;
  run_config?: Record<string, unknown>;
  tags?: Record<string, string>;
}

export interface LaunchRunResponse {
  run_id: string;
  error: string | null;
}

export function isRunActive(status: RunStatus): boolean {
  return ['NOT_STARTED', 'STARTING', 'STARTED', 'QUEUED', 'CANCELING', 'MANAGED'].includes(status);
}

// ── Centralized Status Styles ───────────────────────────────────────────────
// These were previously duplicated across multiple component files.

export const RUN_STATUS_STYLES: Record<RunStatus, { background: string; color: string }> = {
  SUCCESS: NEUTRAL_PILL,
  FAILURE: FAULT_PILL,
  STARTED: RUNNING_PILL,
  STARTING: NEUTRAL_PILL,
  QUEUED: NEUTRAL_PILL,
  NOT_STARTED: NEUTRAL_PILL,
  CANCELING: NEUTRAL_PILL,
  CANCELED: NEUTRAL_PILL,
  MANAGED: RUNNING_PILL,
};

/** Sentence-case words for Dagster run statuses. */
export const RUN_STATUS_LABELS: Record<RunStatus, string> = {
  SUCCESS: 'Success',
  FAILURE: 'Failure',
  STARTED: 'Started',
  STARTING: 'Starting',
  QUEUED: 'Queued',
  NOT_STARTED: 'Not started',
  CANCELING: 'Canceling',
  CANCELED: 'Canceled',
  MANAGED: 'Managed',
};

export const OP_STATUS_STYLES: Record<OpStatus, { background: string; color: string }> = {
  queued: NEUTRAL_PILL,
  running: RUNNING_PILL,
  completed: NEUTRAL_PILL,
  failed: FAULT_PILL,
  skipped: NEUTRAL_PILL,
};

export const OP_STATUS_LABELS: Record<OpStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  skipped: 'Skipped',
};

export const ARTIFACT_STATUS_STYLES: Record<ArtifactStatus, { background: string; color: string }> = {
  ready: NEUTRAL_PILL,
  producing: RUNNING_PILL,
  failed: FAULT_PILL,
};

export const ARTIFACT_STATUS_LABELS: Record<ArtifactStatus, string> = {
  producing: 'Producing',
  ready: 'Ready',
  failed: 'Failed',
};

export const DEFAULT_STATUS_STYLE: { background: string; color: string } = {
  ...OFFLINE_PILL,
};

export const GPU_STATUS_LABELS: Record<GpuJobStatus, string> = {
  queued: 'Waiting for GPU availability...',
  provisioning: 'GPU instance launching...',
  active: 'GPU instance ready, starting pipeline...',
  running: 'Training in progress',
  terminating: 'Shutting down GPU...',
  terminated: 'GPU terminated',
  failed: 'GPU provisioning failed',
  cancelled: 'Cancelled',
};

/**
 * Log level colours for terminal-style log viewers. A log viewer has an
 * ink fill in both themes, so these are the terminal tokens. An error is
 * the brightest line and a warning is the next.
 */
export const LOG_LEVEL_COLORS: Record<string, string> = {
  ERROR: 'var(--color-terminal-error)',
  WARNING: 'var(--color-terminal-text)',
  DEFAULT: 'var(--color-terminal-muted)',
};

// ── Shared Formatters ───────────────────────────────────────────────────────

/** Format a duration from start/end timestamps (seconds or ISO strings) */
export function formatDuration(
  startIso: string | number | null,
  endIso?: string | number | null,
): string {
  if (!startIso) return '—';
  const start = typeof startIso === 'number' ? startIso * 1000 : new Date(startIso).getTime();
  const end = endIso
    ? typeof endIso === 'number'
      ? endIso * 1000
      : new Date(endIso).getTime()
    : Date.now();
  const seconds = Math.max(0, Math.floor((end - start) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

/** Format a timestamp (seconds since epoch) to a localized time string */
export function formatTime(timestamp: number | null): string {
  if (!timestamp) return '—';
  return new Date(timestamp * 1000).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Format an ISO string to a localized time-only string */
export function formatIsoTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString();
}
